import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Tab,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  Tooltip,
  Pagination,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  MenuBook,
  AccessTime,
  Person,
  Drafts,
  Public,
  Warning,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { lessonsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import Card from '../components/UIKit/Card';
import Tabs from '../components/UIKit/Tabs';
import Button from '../components/UIKit/Button';
import SearchInput from '../components/UIKit/SearchInput';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../context/LanguageContext';
import SchoolIcon from '@mui/icons-material/School';

const MyLessons = () => {
  const [tabValue, setTabValue] = useState(0);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const itemsPerPage = 10;
  
  const { t } = useTranslation(['lessons', 'common']);
  const { currentLanguage } = useLanguage();

  // Диалог подтверждения удаления
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [lessonToDelete, setLessonToDelete] = useState(null);
  
  // Двойное подтверждение удаления
  const [doubleConfirmOpen, setDoubleConfirmOpen] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [userConfirmationText, setUserConfirmationText] = useState('');

  const [coursesDialogOpen, setCoursesDialogOpen] = useState(false);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [lessonCourses, setLessonCourses] = useState([]);

  const fetchMyLessons = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      // Параметры пагинации и поиска
      const params = {
        page,
        limit: itemsPerPage,
        search: search || undefined,
      };

      let response;
      if (tabValue === 0) {
        // Все мои уроки
        response = await lessonsAPI.getMy(params);
      } else if (tabValue === 1) {
        // Черновики
        response = await lessonsAPI.getMyDrafts(params);
      } else if (tabValue === 2) {
        // Опубликованные
        response = await lessonsAPI.getMyPublished(params);
      }

      // Обработка ответа с пагинацией
      let lessonsData = [];
      if (response && response.data) {
        lessonsData = response.data.data || [];
        const paginationInfo = response.data.pagination || {
          total: 0,
          pages: 1,
        };
        setTotalPages(paginationInfo.pages);
      }

      setLessons(lessonsData);
    } catch (err) {
      console.error('Error loading lessons:', err);
      setError(t('lessons:myLessons.errors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [tabValue, page, search, t]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchMyLessons();
  }, [isAuthenticated, navigate, tabValue, page, search, t, fetchMyLessons]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(1); // Сбрасываем страницу при смене вкладки
  };

  const handlePageChange = (event, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = (lesson) => {
    setLessonToDelete(lesson);
    setConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!lessonToDelete) return;

    try {
      const response = await lessonsAPI.delete(lessonToDelete._id);
      
      // Если удаление прошло успешно
      setLessons(lessons.filter((lesson) => lesson._id !== lessonToDelete._id));
      toast.success(t('lessons:myLessons.success.deleted'));
      setConfirmOpen(false);
      setLessonToDelete(null);
      
    } catch (err) {
      console.error('Error deleting lesson:', err);
      
      // Проверяем, есть ли зависимости
      if (err.response?.data?.code === 'LESSON_HAS_DEPENDENCIES') {
        // Показываем информацию о зависимостях
        const dependencies = err.response.data.dependencies;
        setLessonCourses(dependencies.courses);
        setSelectedLesson(lessonToDelete);
        setCoursesDialogOpen(true);
        
        // Закрываем диалог подтверждения
        setConfirmOpen(false);
        setLessonToDelete(null);
        
        toast.error(err.response.data.message);
        return;
      }
      
      // Для других ошибок показываем стандартное сообщение
      toast.error(err.response?.data?.error || t('lessons:myLessons.errors.deleteError'));
    }
  };

  // Обработчик первого подтверждения - проверяем зависимости
  const handleFirstDeleteConfirm = async () => {
    if (!lessonToDelete) return;

    try {
      // Сначала проверяем зависимости через API
      const response = await lessonsAPI.getCourses(lessonToDelete._id);
      
      if (response.data.success && response.data.data.length > 0) {
        // Есть зависимости - показываем их
        setLessonCourses(response.data.data);
        setSelectedLesson(lessonToDelete);
        setCoursesDialogOpen(true);
        
        // Закрываем диалог подтверждения
        setConfirmOpen(false);
        setLessonToDelete(null);
        
        toast.error(`Урок используется в ${response.data.data.length} курс${response.data.data.length === 1 ? 'е' : 'ах'} и не может быть удален`);
        return;
      }
      
      // Нет зависимостей - переходим к двойному подтверждению
      setDeleteConfirmationText(lessonToDelete.title);
      setConfirmOpen(false);
      setDoubleConfirmOpen(true);
      
    } catch (err) {
      console.error('Error checking lesson dependencies:', err);
      // Если не удалось проверить зависимости, переходим к двойному подтверждению
      setDeleteConfirmationText(lessonToDelete.title);
      setConfirmOpen(false);
      setDoubleConfirmOpen(true);
    }
  };

  // Обработчик двойного подтверждения удаления
  const handleDoubleDeleteConfirm = async () => {
    if (!lessonToDelete || userConfirmationText !== deleteConfirmationText) {
      toast.error('Текст подтверждения не совпадает');
      return;
    }

    try {
      await lessonsAPI.delete(lessonToDelete._id);
      setLessons(lessons.filter((lesson) => lesson._id !== lessonToDelete._id));
      toast.success(t('lessons:myLessons.success.deleted'));
    } catch (err) {
      console.error('Error deleting lesson:', err);
      toast.error(err.response?.data?.error || t('lessons:myLessons.errors.deleteError'));
    } finally {
      setDoubleConfirmOpen(false);
      setLessonToDelete(null);
      setUserConfirmationText('');
      setDeleteConfirmationText('');
    }
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
    fetchMyLessons();
  };

  // Функция для форматирования даты
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const locale = currentLanguage === 'ru' ? 'ru-RU' : 'en-US';
    
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  // Функция для определения цвета статуса урока
  const getLessonStatusColor = (isDraft) => {
    return isDraft ? 'warning' : 'success';
  };

  // Функция для отображения текста статуса
  const getLessonStatusText = (isDraft) => {
    return isDraft
      ? t('lessons:card.status.draft')
      : t('lessons:card.status.published');
  };

  // Функция для просмотра курсов, связанных с уроком
  const handleViewCourses = async (lesson) => {
    try {
      setSelectedLesson(lesson);
      setCoursesDialogOpen(true);

      // Запрашиваем реальные данные о курсах
      const response = await lessonsAPI.getCourses(lesson._id);
      if (response.data.success) {
        setLessonCourses(response.data.data);
      } else {
        setLessonCourses([]);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error(t('lessons:myLessons.errors.loadCoursesError'));
      setLessonCourses([]);
    }
  };

  return (
    <Container sx={{ py: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        mb={2}
      >
        <Box display="flex" alignItems="center">
          <MenuBook fontSize="large" color="primary" sx={{ mr: 2 }} />
          <Typography variant="h3" component="h1">
            {t('lessons:myLessons.title')}
          </Typography>
        </Box>

        <Box>
          <Button
            variant="outlined"
            color="primary"
            component={Link}
            to="/lessons"
            sx={{ mr: 2 }}
          >
            {t('lessons:myLessons.allLessons')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            component={Link}
            to="/lessons/create"
          >
            {t('lessons:myLessons.createLesson')}
          </Button>
        </Box>
      </Box>

      <Box mb={3}>
        <Typography variant="body1" color="text.secondary" paragraph>
          {t('lessons:myLessons.subtitle')}
        </Typography>
      </Box>

      <Box mb={3}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab icon={<MenuBook />} label={t('lessons:myLessons.tabs.all')} />
          <Tab icon={<Drafts />} label={t('lessons:myLessons.tabs.drafts')} />
          <Tab icon={<Public />} label={t('lessons:myLessons.tabs.published')} />
        </Tabs>
      </Box>

      <Box mb={4}>
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          placeholder={t('lessons:myLessons.searchPlaceholder')}
          debounceTime={500}
        />
      </Box>

      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
        >
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
      ) : !Array.isArray(lessons) || lessons.length === 0 ? (
        <Alert severity="info" sx={{ mb: 4 }}>
          {tabValue === 0
            ? t('lessons:myLessons.noLessons')
            : tabValue === 1
            ? t('lessons:myLessons.noDrafts')
            : t('lessons:myLessons.noPublished')}
        </Alert>
      ) : (
        <>
          <Grid container spacing={3}>
            {lessons.map((lesson) => (
              <Grid item xs={12} md={6} key={lesson._id}>
                <Card sx={{ p: 1, height: '100%' }}>
                  <CardContent>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Typography variant="h5" component="div" gutterBottom>
                        {lesson.title}
                      </Typography>
                      <Chip
                        label={getLessonStatusText(lesson.isDraft)}
                        color={getLessonStatusColor(lesson.isDraft)}
                        size="small"
                      />
                    </Box>

                    <Box display="flex" alignItems="center" mb={1}>
                      <Person
                        fontSize="small"
                        sx={{ mr: 1, color: 'text.secondary' }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {lesson.instructor?.userMeta?.fullName || t('lessons:card.unknown')}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" mb={1}>
                      <AccessTime
                        fontSize="small"
                        sx={{ mr: 1, color: 'text.secondary' }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {lesson.duration} {t('common:time.minutes')}
                      </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary">
                      {t('lessons:card.created')}: {formatDate(lesson.createdAt)}
                    </Typography>

                    {lesson.updatedAt &&
                      lesson.updatedAt !== lesson.createdAt && (
                        <Typography variant="body2" color="text.secondary">
                          {t('lessons:card.updated')}:{' '}
                          {formatDate(lesson.updatedAt)}
                        </Typography>
                      )}

                    <Typography variant="body2">
                      {t('lessons:duration')}: {lesson.duration || 0} {t('common:time.shortMin')}
                    </Typography>
                  </CardContent>
                  <Divider />
                  <CardActions>
                    <Button
                      size="small"
                      variant="text"
                      startIcon={<Visibility />}
                      component={Link}
                      to={`/lessons/${lesson.slug || lesson._id}`}
                    >
                      {t('lessons:myLessons.buttons.view')}
                    </Button>

                    <Button
                      size="small"
                      variant="text"
                      startIcon={<Edit />}
                      component={Link}
                      to={`/lessons/edit/${lesson.slug || lesson._id}`}
                    >
                      {t('lessons:myLessons.buttons.edit')}
                    </Button>

                    <Button
                      size="small"
                      variant="text"
                      startIcon={<Delete />}
                      color="error"
                      onClick={() => handleDeleteClick(lesson)}
                    >
                      {t('lessons:myLessons.buttons.delete')}
                    </Button>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}
                    >
                      {lesson.coursesCount !== undefined && (
                        <Typography
                          variant="body2"
                          sx={{
                            mr: 1,
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                          }}
                        >
                          {lesson.coursesCount} курс{lesson.coursesCount === 1 ? '' : lesson.coursesCount < 5 ? 'а' : 'ов'}
                        </Typography>
                      )}
                      {lesson.modulesCount !== undefined && (
                        <Typography
                          variant="body2"
                          sx={{
                            mr: 1,
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                          }}
                        >
                          {lesson.modulesCount} модул{lesson.modulesCount === 1 ? 'ь' : lesson.modulesCount < 5 ? 'я' : 'ей'}
                        </Typography>
                      )}
                      <Tooltip title={t('lessons:myLessons.coursesDialog.tooltip')}>
                        <IconButton
                          size="small"
                          onClick={() => handleViewCourses(lesson)}
                        >
                          <SchoolIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="medium"
              />
            </Box>
          )}
        </>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Подтверждение удаления урока"
        content={
          lessonToDelete
            ? `Вы уверены, что хотите удалить урок "${lessonToDelete.title}"? Это действие нельзя отменить.`
            : ''
        }
        onConfirm={handleFirstDeleteConfirm}
        onCancel={() => {
          setConfirmOpen(false);
          setLessonToDelete(null);
        }}
        confirmText="Продолжить"
        cancelText={t('common:actions.cancel')}
      />

      {/* Диалог с информацией о курсах */}
      <Dialog
        open={coursesDialogOpen}
        onClose={() => setCoursesDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '16px',
            p: 1,
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Warning color="warning" sx={{ mr: 1 }} />
            {selectedLesson
              ? `Урок "${selectedLesson.title}" используется в курсах`
              : 'Зависимости урока'}
          </Box>
        </DialogTitle>
        <DialogContent>
          {lessonCourses.length === 0 ? (
            <Typography>{t('lessons:myLessons.coursesDialog.noCourses')}</Typography>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Этот урок не может быть удален, так как он используется в следующих курсах. 
                  Сначала удалите урок из всех модулей этих курсов.
                </Typography>
              </Alert>
              <List>
                {lessonCourses.map((course) => (
                  <ListItem key={course.id || course._id} divider>
                    <ListItemIcon>
                      <SchoolIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={course.title}
                      secondary={
                        <Box>
                          {course.description && (
                            <Typography variant="body2" color="text.secondary">
                              {course.description}
                            </Typography>
                          )}
                          {course.instructor && (
                            <Typography variant="caption" color="text.secondary">
                              Преподаватель: {course.instructor.userMeta?.fullName || 'Неизвестно'}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setCoursesDialogOpen(false);
                        navigate(`/courses/${course.slug || course._id}`);
                      }}
                    >
                      Открыть курс
                    </Button>
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setCoursesDialogOpen(false)}
          >
            {t('common:actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог двойного подтверждения удаления */}
      <Dialog
        open={doubleConfirmOpen}
        onClose={() => {
          setDoubleConfirmOpen(false);
          setLessonToDelete(null);
          setUserConfirmationText('');
          setDeleteConfirmationText('');
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: '16px',
            p: 1,
          },
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Warning color="error" sx={{ mr: 1 }} />
            Окончательное подтверждение удаления
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Вы собираетесь окончательно удалить урок "{lessonToDelete?.title}". 
              Это действие нельзя отменить.
            </Typography>
          </Alert>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Для подтверждения введите название урока:
          </Typography>
          
          <TextField
            fullWidth
            label={`Введите "${lessonToDelete?.title}"`}
            value={userConfirmationText}
            onChange={(e) => setUserConfirmationText(e.target.value)}
            variant="outlined"
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setDoubleConfirmOpen(false);
              setLessonToDelete(null);
              setUserConfirmationText('');
              setDeleteConfirmationText('');
            }}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDoubleDeleteConfirm}
            disabled={userConfirmationText !== lessonToDelete?.title}
          >
            Удалить навсегда
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MyLessons;
